import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Download, Mail } from 'lucide-react';
import { useVerifyPaymentSessionMutation } from '../../hooks/queries/payment';
import { useUserApplication } from '../../context/UserApplicationContext';
import { useUser } from '@clerk/clerk-react';

const PaymentSuccess = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [sessionId, setSessionId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [paymentAmount, setPaymentAmount] = useState(null);
    const { currentApplicationId } = useUserApplication();
    const { user } = useUser();
    const verifyPaymentMutation = useVerifyPaymentSessionMutation();

    useEffect(() => {
        const sessionIdFromUrl = searchParams.get('session_id');
        if (sessionIdFromUrl) {
            setSessionId(sessionIdFromUrl);
            // Verify payment status with backend
            verifyPaymentStatus(sessionIdFromUrl);
        } else {
            // No session ID, redirect to dashboard
            navigate('/dashboard');
        }
    }, [searchParams, navigate]);

    const verifyPaymentStatus = async (sessionId) => {
        try {
            console.log('🔍 PaymentSuccess: Starting payment verification for session:', sessionId);

            const result = await verifyPaymentMutation.mutateAsync({
                sessionId,
                applicationId: currentApplicationId,
                userId: user?.id,
            });

            if (result?.session?.amount_total) {
                const amountInDollars = (result.session.amount_total / 100).toFixed(2);
                setPaymentAmount(amountInDollars);
                console.log('💰 PaymentSuccess: Payment amount set to:', amountInDollars);
            }

            if (result?.paymentStatus === 'paid') {
                console.log('🎉 PaymentSuccess: Payment confirmed as paid!');
            } else {
                console.log('⚠️ PaymentSuccess: Payment not yet paid, status:', result?.paymentStatus);
            }
        } catch (error) {
            console.error('💥 PaymentSuccess: Error verifying payment:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReturnToDashboard = () => {
        // Navigate to the Quote & Payment section (shows filing status once paid)
        navigate('/dashboard#payments');
    };

    if (isLoading || verifyPaymentMutation.isPending) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Processing your payment...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
                {/* Success Icon */}
                <div className="mb-6">
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">
                        Payment Successful!
                    </h1>
                    <p className="text-gray-600">
                        Your trademark application payment has been processed successfully.
                    </p>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
                    <h2 className="font-semibold text-gray-900 mb-2">Payment Details</h2>
                    <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex justify-between">
                            <span>Amount:</span>
                            <span className="font-medium">
                                {paymentAmount ? `$${paymentAmount} USD` : 'Loading...'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span>Transaction ID:</span>
                            <span className="font-mono text-xs">{sessionId.slice(-12)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span>Status:</span>
                            <span className="text-green-600 font-medium">Completed</span>
                        </div>
                    </div>
                </div>

                {/* Next Steps */}
                <div className="mb-6 text-left">
                    <h3 className="font-semibold text-gray-900 mb-3">What's Next?</h3>
                    <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-start space-x-3">
                            <Mail className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>You'll receive a confirmation email with your receipt</span>
                        </div>
                        <div className="flex items-start space-x-3">
                            <Download className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>Your trademark application will be processed and filed</span>
                        </div>
                        <div className="flex items-start space-x-3">
                            <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>Track your application status in your dashboard</span>
                        </div>
                    </div>
                </div>

                {/* Action Button */}
                <button
                    onClick={handleReturnToDashboard}
                    className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center space-x-2 font-medium"
                >
                    <span>View Filing Status</span>
                    <ArrowRight className="h-4 w-4" />
                </button>

                {/* Support Note */}
                <p className="mt-6 text-xs text-gray-500">
                    Questions? Contact our support team at{' '}
                    <a href="mailto:support@markotm.com" className="text-blue-600 hover:underline">
                        support@markotm.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default PaymentSuccess;
